<template>
  <div class="dashboard-page container py-4">
    
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid p-4" id="main-content">
      
      <h1 class="mb-4"><font-awesome-icon icon="tachometer-alt" /> Dashboard</h1>

      <!-- Aktionen erforderlich / Hinweise -->
      <div class="mb-3">
        <!-- Getting Started Tutorial für neue Benutzer -->
        <div v-if="!hasCompletedTutorial" class="alert alert-info alert-dismissible mb-3" role="alert">
          <div class="d-flex align-items-center gap-2">
            <font-awesome-icon icon="graduation-cap" class="fs-5" />
            <div class="flex-grow-1">
              <strong>Willkommen bei Quitly!</strong> Lerne die wichtigsten Funktionen in unserem Getting Started Tutorial kennen.
            </div>
            <div class="d-flex gap-2">
              <router-link to="/getting-started" class="btn btn-primary btn-sm">
                <font-awesome-icon icon="play" class="me-1" />
                Tutorial starten
              </router-link>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm"
                @click="dismissTutorialHint"
              >
                <font-awesome-icon icon="times" class="me-1" />
                Später
              </button>
            </div>
          </div>
        </div>
        <!-- Bereich-Einladung verfügbar (bestehender Hinweis bleibt) -->
      </div>

      <!-- Ausstehende Bereich-Bestätigungen -->
      <div v-if="totalPendingApprovals > 0" class="alert alert-gradient-warning alert-dismissible mb-3" role="alert">
        <div class="d-flex align-items-center gap-2">
          <font-awesome-icon icon="user-clock" class="fs-5" />
          <div class="flex-grow-1">
            <strong>{{ totalPendingApprovals }}</strong> ausstehende Beitrittsanfrage(n) in deinen Bereichen.
          </div>
          <router-link class="btn btn-sm btn-primary" :to="{ name: 'teams-list' }">
            Zu den Bereichen
          </router-link>
        </div>
      </div>

      <!-- Fehlende Pflichtfelder -->
      <div v-if="teamsWithMissingFields.length > 0" class="alert alert-gradient-warning alert-dismissible mb-3" role="alert">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <font-awesome-icon icon="exclamation-triangle" class="fs-5" />
          <div class="flex-grow-1">
            In folgenden Bereichen fehlen Pflichtfelder: 
            <span class="fw-semibold">{{ teamsWithMissingFields.map(t => t.name).join(', ') }}</span>
          </div>
          <div class="d-flex gap-2">
            <button v-for="team in teamsWithMissingFields" :key="team.id" class="btn btn-sm btn-primary" @click="goToTeamFields(team.id)">
              <font-awesome-icon icon="edit" class="me-1" />
              Felder ausfüllen
            </button>
          </div>
        </div>
      </div>

      <!-- Fehlende Erstattungs-/Zahlungsdaten -->
      <div v-if="refundDataMissingTeams.length > 0" class="alert alert-info alert-dismissible mb-4" role="alert">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <font-awesome-icon icon="money-bill-wave" class="fs-5" />
          <div class="flex-grow-1">
            Erstattungs-/Zahlungsdaten fehlen in: 
            <span class="fw-semibold">{{ refundDataMissingTeams.map(t => t.name).join(', ') }}</span>
          </div>
          <div class="d-flex gap-2">
            <button v-for="team in refundDataMissingTeams" :key="team.id" class="btn btn-sm btn-outline-info" @click="goToTeamRefund(team.id)">
              {{ team.name }} Zahlungsdaten
            </button>
          </div>
        </div>
      </div>

      <!-- Invitation Code Notification -->
      <div v-if="pendingInvitationCode" class="alert alert-info alert-dismissible mb-4" role="alert">
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="user-plus" class="me-3 fs-4" />
          <div class="flex-grow-1">
            <h5 class="alert-heading mb-1">Bereich-Einladung verfügbar</h5>
            <p class="mb-2">Du hast einen gespeicherten Einladungscode für einen Bereich.</p>
            <div class="d-flex gap-2">
              <router-link 
                :to="{ name: 'teams-join' }" 
                class="btn btn-success btn-sm"
                @click="clearInvitationNotification"
              >
                <font-awesome-icon icon="user-plus" class="me-1" />
                Bereich beitreten
              </router-link>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm"
                @click="dismissInvitationCode"
              >
                <font-awesome-icon icon="times" class="me-1" />
                Hinweis entfernen
              </button>
            </div>
          </div>
        </div>
      </div>

      
      <div class="row mb-4">
        <div class="col-lg-7 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <h2 class="card-title mb-2">Schnellstart</h2>
              <p class="text-muted">Erstelle jetzt deine nächste Quittung.</p>
              <div class="d-flex flex-wrap gap-2">
                <button class="btn btn-primary btn-lg" @click="handleCreateReceipt">
                  <font-awesome-icon icon="plus" class="me-2" />
                  Neue Quittung
                </button>
                <router-link to="/receipts" class="btn btn-outline-primary btn-lg">
                  <font-awesome-icon icon="list" class="me-2" />
                  Quittungen ansehen
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-5 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <h2 class="card-title mb-3">Guthaben</h2>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div class="fw-semibold text-muted small">Persönlich</div>
                  <div class="h3 fw-bold mb-0 coins">{{ user?.account?.amount || 0 }}</div>
                </div>
                <div>
                  <div class="fw-semibold text-muted small">Bereiche gesamt</div>
                  <div class="h3 fw-bold mb-0 coins">{{ totalTeamCoins }}</div>
                </div>
                <div>
                  <button class="btn btn-outline-primary" @click="handleBuyTokens">
                    Quittungen kaufen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Letzte Aktivitäten</h2>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Titel</th>
                  <th>Betrag</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="receipts.length === 0">
                  <td colspan="5" class="text-center py-3">Keine Aktivitäten vorhanden</td>
                </tr>
                <tr v-for="receipt in recentReceipts" :key="receipt.id">
                  <td>{{ formatDate(receipt.createdAt) }}</td>
                  <td>{{ receipt.title }}</td>
                  <td>{{ formatCurrency(receipt.amount) }}</td>
                  <td>
                    <span :class="getStatusClass(receipt.status)">{{ getStatusText(receipt.status) }}</span>
                  </td>
                  <td>
                    <div class="d-flex gap-2">
                      <button class="btn btn-sm btn-outline-primary" @click="viewReceipt(receipt.id)">
                        <font-awesome-icon icon="eye" class="me-1" />Ansehen
                      </button>
                      <button v-if="receipt.status !== 'canceled' && receipt.status !== 'rejected'" class="btn btn-sm btn-outline-secondary" @click="downloadReceipt(receipt.id)">
                        <font-awesome-icon icon="download" class="me-1" />Herunterladen
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end">
            <router-link to="/receipts" class="btn btn-outline-primary">
              Alle Quittungen <font-awesome-icon icon="arrow-right" class="ms-1" />
            </router-link>
          </div>
        </div>
      </div>

      <!-- Benachrichtigungen -->
      <div v-if="recentNotifications.length > 0" class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h2 class="card-title mb-0">
              <font-awesome-icon icon="bell" class="me-2" />
              Neueste Benachrichtigungen
            </h2>
            <div class="d-flex gap-2">
              <router-link to="/notifications" class="btn btn-outline-primary btn-sm">
                Alle ansehen
              </router-link>
              <button 
                type="button" 
                class="btn btn-outline-secondary btn-sm"
                @click="markAllAsRead"
                :disabled="markingAllAsRead"
              >
                <font-awesome-icon icon="check-double" class="me-1" />
                Alle als gelesen markieren
              </button>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Titel</th>
                  <th>Nachricht</th>
                  <th>Zeit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="notification in recentNotifications.slice(0, 3)" 
                  :key="notification.id"
                  @click="handleNotificationClick(notification)"
                  style="cursor: pointer;"
                  class="notification-row"
                >
                  <td>
                    <font-awesome-icon 
                      :icon="getNotificationIcon(notification.type)" 
                      :class="getNotificationIconClass(notification.type)"
                      class="fs-5"
                    />
                  </td>
                  <td>
                    <strong>{{ notification.title }}</strong>
                  </td>
                  <td>
                    <span class="text-muted">{{ notification.message }}</span>
                  </td>
                  <td>
                    <small class="text-muted">{{ formatTime(notification.createdAt) }}</small>
                  </td>
                  <td>
                    <span v-if="!notification.readAt" class="badge bg-warning">Ungelesen</span>
                    <span v-else class="badge bg-success">Gelesen</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Purchase History -->
      <PurchaseHistory :limit="5" :show-title="true" />
    </div>

    <!-- Transfer Dialog -->
    <CoinTransferDialog
      v-if="selectedTeam"
      :show="showTransferDialog"
      :team-id="selectedTeam.id"
      :personal-coins="user?.account?.amount || 0"
      :team-coins="selectedTeam.account?.amount || 0"
      @close="showTransferDialog = false"
      @transfer-completed="handleTransferCompleted"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { showError, showSuccess } from '@/services/notification'
import { storeToRefs } from 'pinia'
import CoinTransferDialog from '@/components/coins/CoinTransferDialog.vue'
import PurchaseHistory from '@/components/PurchaseHistory.vue'
import { coinTransferApi } from '@/api/coinTransfer'
import { receiptsApi } from '@/api/receipts'
import { paypalBusinessApi } from '@/api/paypalBusiness'
import { useTeamsStore } from '@/stores/teams'
import { teamService } from '@/api/teams'
import { getNotifications, markAllNotificationsAsRead } from '@/api/notifications'
import notificationService from '@/services/notificationService'
import { markTutorialComplete } from '@/api/auth'


const router = useRouter()
const authStore = useAuthStore()
const {user} = storeToRefs(authStore)
const teamsStore = useTeamsStore()


const pendingInvitationCode = computed(() => {
  return authStore.getPendingInvitationCode()
})

const hasCompletedTutorial = computed(() => {
  return user.value?.tutorial === true
})


const userTeams = computed(() => {
  return user.value?.roles?.map(role => role.team) || []
})

const totalTeamCoins = computed(() => {
  return user.value?.roles?.reduce((total, role) => total + (role.team?.account?.amount || 0), 0) || 0
})
const tokens = ref(0)
const receipts = ref([])
const recentActivity = ref([])
const loading = ref(false)
const recentReceipts = ref([])


const pendingApprovalsPerTeam = ref(new Map())
const teamsWithMissingFields = ref([])
const refundDataMissingTeams = ref([])


const showTransferDialog = ref(false)
const selectedTeam = ref(null)
const availableForTransfer = ref([])


const recentNotifications = ref([])
const markingAllAsRead = ref(false)


const loadNotifications = async () => {
  try {
    const response = await getNotifications({ limit: 5, unreadOnly: false })
    recentNotifications.value = response.data.data.notifications || []
  } catch (error) {
    console.error('Fehler beim Laden der Benachrichtigungen:', error)
  }
}


const markAllAsRead = async () => {
  try {
    markingAllAsRead.value = true
    await markAllNotificationsAsRead()
    recentNotifications.value = []
    showSuccess('Alle Benachrichtigungen als gelesen markiert')
  } catch (error) {
    console.error('Fehler beim Markieren aller Benachrichtigungen:', error)
    showError('Fehler beim Markieren der Benachrichtigungen')
  } finally {
    markingAllAsRead.value = false
  }
}

const loadDashboardData = async () => {
  try {
    loading.value = true
    
    if (authStore.user) {
      user.value = authStore.user
    }
    

    try {
      const receiptsResponse = await receiptsApi.getUserReceipts({ 
        page: 1, 
        limit: 10 
      })
      
      if (receiptsResponse.receipts) {
        receipts.value = receiptsResponse.receipts
        recentReceipts.value = receiptsResponse.receipts.slice(0, 5)
    }

    await Promise.all([
      loadTeamsSignals(),
      loadNotifications()
    ])
    } catch (error) {
      console.error('Error loading receipts:', error)

      receipts.value = []
      recentReceipts.value = []
    }
    
  } catch (error) {
    console.error('Dashboard error:', error)
    showError('Fehler beim Laden der Dashboard-Daten')
  } finally {
    loading.value = false
  }
}

const handleBuyTokens = () => {
  router.push('/account')
}

const handleCreateReceipt = () => {
  router.push('/receipts/create')
}



const viewReceipt = async (receiptId) => {
  try {

    router.push(`/receipts/${receiptId}`)
  } catch (error) {
    showError('Fehler beim Laden der Quittung')
    console.error('View receipt error:', error)
  }
}

const downloadReceipt = async (receiptId) => {
  try {
    await receiptsApi.downloadReceiptPDF(receiptId)
    showSuccess('Quittung wird heruntergeladen...')
  } catch (error) {
    console.error('Download error:', error)
    showError('Fehler beim Herunterladen der Quittung')
  }
}

const getStatusClass = (status) => {
  switch (status) {
    case 'draft':
      return 'badge bg-info'
    case 'final':
      return 'badge bg-success'
    case 'pending':
      return 'badge bg-warning'
    case 'approved':
      return 'badge bg-success'
    case 'rejected':
      return 'badge bg-danger'
    default:
      return 'badge bg-secondary'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'draft':
      return 'Entwurf'
    case 'final':
      return 'Aktiv'
    case 'pending':
      return 'Ausstehend'
    case 'approved':
      return 'Genehmigt'
    case 'rejected':
      return 'Abgelehnt'
    default:
      return status
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE')
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}


const clearInvitationNotification = () => {


}

const dismissInvitationCode = () => {
  authStore.clearPendingInvitationCode()
  showSuccess('Einladungscode wurde entfernt')
}

const dismissTutorialHint = async () => {
  try {
    await markTutorialComplete()
    // Profil neu laden, damit die Änderung sofort sichtbar wird
    await authStore.updateProfile()
    showSuccess('Tutorial-Hinweis entfernt. Du kannst es jederzeit über das Profil starten.')
  } catch (error) {
    console.error('Error dismissing tutorial hint:', error)
    showError('Fehler beim Entfernen des Tutorial-Hinweises')
  }
}


const openTransferDialog = (team) => {
  selectedTeam.value = team
  showTransferDialog.value = true
}

const handleTransferCompleted = async (transferData) => {

  await loadDashboardData()
  await loadAvailableForTransfer()
  showSuccess(`Transfer von ${transferData.amount} Quittungen erfolgreich!`)
}

const loadAvailableForTransfer = async () => {
  try {
    const availableAmounts = []
    
    for (const team of userTeams.value) {
      try {
        const response = await coinTransferApi.getAvailableCoins(team.id)
        if (response.totalAvailable > 0) {
          availableAmounts.push({
            teamId: team.id,
            teamName: team.name,
            amount: response.totalAvailable
          })
        }
      } catch (error) {

        console.warn(`Could not load available coins for team ${team.name}:`, error)
      }
    }
    
    availableForTransfer.value = availableAmounts
  } catch (error) {
    console.error('Error loading available transfer amounts:', error)
  }
}


const totalPendingApprovals = computed(() => {
  let total = 0
  pendingApprovalsPerTeam.value.forEach((count) => {
    total += count || 0
  })
  return total
})

const goToTeamFields = (teamId) => {
  if (!teamId) return
  router.push({ name: 'teams-fields', params: { id: teamId } })
}

const goToTeamRefund = (teamId) => {
  if (!teamId) return
  router.push({ name: 'teams-refund', params: { id: teamId } })
}

const isTeamAdminOrCreator = (team) => {
  if (!team || !user.value) return false
  return team.role === 'teamAdmin' || team.creatorId === user.value.id
}

const hasMissingRequiredFieldsForCurrentUser = (team) => {
  if (!team?.fields || !user.value) return false
  return team.fields.some((field) => {
    if (!field.required) return false
    if (field.responses && Array.isArray(field.responses)) {
      const resp = field.responses.find((r) => r.userId === user.value.id)
      return !resp || !resp.data || resp.data === ''
    }
    if (Array.isArray(field.response)) {
      return field.response.length === 0 || !field.response[0]?.data
    }
    return true
  })
}

const checkRefundDataRequirementForTeam = async (team) => {
  try {
    if (!team) return false
    if (team.status !== 'pro' && team.status !== 'ultimate') return false
    const refundSettings = await teamService.getTeamRefundSettings(team.id)
    if (!refundSettings?.enabled) return false
    const currentData = await teamService.getUserRefundData(team.id)
    if (currentData?.refundMethod === 'paypal') {
      return !currentData.paypalMeName
    } else if (currentData?.refundMethod === 'bank_transfer') {
      return !currentData.iban || !currentData.bic || !currentData.accountHolder
    }
    return !currentData?.refundMethod
  } catch (e) {
    console.warn('Refund-Check fehlgeschlagen:', e)
    return false
  }
}

const loadTeamsSignals = async () => {
  try {
    await teamsStore.fetchTeams()
    const teams = teamsStore.teams
    const pendingMap = new Map()
    const missingFieldsTeams = []
    const refundMissing = []

    for (const team of teams) {
      if (isTeamAdminOrCreator(team)) {
        try {
          const resp = await teamService.getTeamJoinRequests(team.id, 'pending', 1, 1)
          const count = resp?.pagination?.total || (resp?.requests?.length || 0)
          if (count > 0) pendingMap.set(team.id, count)
        } catch {}
      }

      if (hasMissingRequiredFieldsForCurrentUser(team)) {
        missingFieldsTeams.push({ id: team.id, name: team.name })
      }

      const refundMissingForTeam = await checkRefundDataRequirementForTeam(team)
      if (refundMissingForTeam) {
        refundMissing.push({ id: team.id, name: team.name })
      }
    }

    pendingApprovalsPerTeam.value = pendingMap
    teamsWithMissingFields.value = missingFieldsTeams
    refundDataMissingTeams.value = refundMissing
  } catch (e) {
    console.warn('Teams-Signale konnten nicht geladen werden:', e)
  }
}


const getTypeDisplayName = (type) => {
  switch (type) {
    case 'coin': return 'Quittung-Kauf'
    case 'team': return 'Team-Upgrade'
    default: return type
  }
}

const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'coin': return 'badge-coin'
    case 'team': return 'badge-team'
    default: return 'badge-secondary'
  }
}


// Verwende Service-Funktionen
const { getNotificationLink, formatTime, getNotificationIcon, getNotificationIconClass } = notificationService




const handleNotificationClick = (notification) => {
  notificationService.handleNotificationClick(notification, () => {}, router)
}



const getBillingStatusText = (status) => {
  switch (status) {
    case 'pending': return 'Ausstehend'
    case 'completed': return 'Abgeschlossen'
    case 'failed': return 'Fehlgeschlagen'
    default: return status
  }
}

const getBillingStatusClass = (status) => {
  switch (status) {
    case 'pending': return 'badge bg-warning'
    case 'completed': return 'badge bg-success'
    case 'failed': return 'badge bg-danger'
    default: return 'badge bg-secondary'
  }
}

const canDownloadInvoice = (billing) => {
  return billing.status === 'completed' || (billing.status === 'pending' && billing.method === 'bank_transfer' && billing.reminderInvoiceNumber)
}

const downloadInvoice = async (billingId, type, invoiceNumber) => {
  try {
    let response
    if (type === 'team') {
      response = await teamBillingApi.downloadTeamBillingInvoice(billingId)
    } else {
      response = await downloadInvoiceApi(billingId)
    }

    const contentDisposition = response.headers?.['content-disposition']
    let filename = `dokument_${invoiceNumber || billingId}.pdf`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }
    const url = window.URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    showSuccess('Dokument wird heruntergeladen...')
  } catch (error) {
    console.error('Download error:', error)
    showError('Fehler beim Herunterladen des Dokuments')
  }
}

const recheckPayPalPayment = async (billingId) => {
  try {
    const { recheckPayPalPayment: recheckApi } = await import('@/api/billing')
    const result = await recheckApi(billingId)
    if (result.status === 'completed') {
      showSuccess('Zahlung erfolgreich bestätigt!')
      await loadRecentBillings()
    } else if (result.status === 'pending') {
      showError('Zahlung ist noch ausstehend. Bitte versuche es später erneut oder gehe zu PayPal.')
    } else {
      showError('Zahlung fehlgeschlagen. Bitte kontaktiere den Support.')
    }
  } catch (error) {
    console.error('PayPal recheck error:', error)
    showError(error.response?.data?.message || 'Fehler beim Überprüfen der PayPal-Zahlung')
  }
}

const goToPayPal = async (billingId) => {
  try {
    const { getPayPalRedirectUrl } = await import('@/api/billing')
    const result = await getPayPalRedirectUrl(billingId)
    window.location.href = result.redirectUrl
  } catch (error) {
    console.error('PayPal redirect error:', error)
    showError(error.response?.data?.message || 'Fehler beim Weiterleiten zu PayPal')
  }
}

const loadRecentBillings = async () => {
  try {
    billingLoading.value = true
    recentBillings.value = []
    let coinResponse = { billings: [] }
    let teamResponse = { teamBillings: [] }
    try {
      coinResponse = await getBillingHistoryApi(1, 10)
    } catch {}
    try {
      teamResponse = await teamBillingApi.getUserTeamBillings(1, 10)
    } catch {}

    const coinBillings = (coinResponse.billings || []).map((b) => ({
      ...b,
      type: 'coin',
      displayName: `${b.coins} Quittungen`,
      customerName: b.User ? `${b.User.firstName} ${b.User.lastName}` : `${b.firstName} ${b.lastName}`,
      customerEmail: b.User ? b.User.email : b.email
    }))
    const teamBillings = (teamResponse.teamBillings || []).map((b) => ({
      ...b,
      type: 'team',
      displayName: `${b.plan === 'pro' ? 'Pro' : 'Ultimate'} Plan`,
      customerName: b.team ? b.team.name : 'Team',
      customerEmail: b.email,
      coins: null
    }))

    const combined = [...coinBillings, ...teamBillings]
    combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    recentBillings.value = combined.slice(0, 5)
  } finally {
    billingLoading.value = false
  }
}

onMounted(() => {
  loadDashboardData()
  loadAvailableForTransfer()
})
</script>

<style scoped>
.notification-row:hover {
  background-color: #f8f9fa !important;
}

.notification-row:hover td {
  background-color: transparent;
}
</style>
